/**
 * Filter Persistence System — Storage Adapter
 *
 * Thin, fault-tolerant wrapper around Web Storage (sessionStorage / localStorage).
 *
 * Design decisions:
 *   • All keys are prefixed with "wp:" so persistence keys are immediately
 *     identifiable and cannot clash with third-party libs or browser internals.
 *   • Every operation is try/caught because:
 *       - Storage may be disabled in private/incognito mode.
 *       - Quota may be exhausted on low-memory devices.
 *       - SSR environments have no `window` object.
 *   • The module is stateless — no singletons, no global caches.
 *     React state is the authoritative in-memory copy; storage is write-through.
 */

import { StorageBackend } from "./types";

const NS_PREFIX = "wp:";

// ─── Runtime-session guard ────────────────────────────────────────────────────
//
// Goal: filters persist through React Router navigation (back/forward, detail
// pages) but are wiped on a browser reload (F5 / Ctrl+R).
//
// How it works:
//   • `RUNTIME_SESSION_ID` is generated fresh every time the JS bundle
//     executes — i.e. once per page load. React Router navigation never
//     re-executes the bundle so this ID stays constant within a session.
//   • `initPersistenceSession()` (called once in App.tsx) compares the
//     in-memory ID to a copy previously stored in sessionStorage.
//       - IDs differ  → fresh page load → wipe all filter storage.
//       - IDs match   → within-session navigation → leave storage intact.
//
// This key is intentionally NOT prefixed with NS_PREFIX so that
// clearAllPersistence() (which removes only NS_PREFIX keys) does not
// accidentally delete it before the comparison is made.
const SESSION_STAMP_KEY = "__wp_runtime_sid__";
const RUNTIME_SESSION_ID = Math.random().toString(36).slice(2, 10);

// ─── Internal helpers ────────────────────────────────────────────────────────

function getStorage(backend: StorageBackend): Storage | null {
  try {
    return backend === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function buildKey(namespace: string, key: string): string {
  return `${NS_PREFIX}${namespace}:${key}`;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Reads a single value from storage. Returns `undefined` if the key
 * does not exist or if deserialisation fails.
 */
export function readFromStorage<T>(
  namespace: string,
  key: string,
  backend: StorageBackend = "session"
): T | undefined {
  const storage = getStorage(backend);
  if (!storage) return undefined;

  try {
    const raw = storage.getItem(buildKey(namespace, key));
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

/**
 * Writes a single value to storage. Silently drops writes when
 * storage is unavailable or quota is exceeded.
 */
export function writeToStorage<T>(
  namespace: string,
  key: string,
  value: T,
  backend: StorageBackend = "session"
): void {
  const storage = getStorage(backend);
  if (!storage) return;

  try {
    storage.setItem(buildKey(namespace, key), JSON.stringify(value));
  } catch {
    // Quota exceeded or private-mode restriction — persist in-memory only.
  }
}

/**
 * Removes a single key from storage.
 */
export function removeFromStorage(
  namespace: string,
  key: string,
  backend: StorageBackend = "session"
): void {
  const storage = getStorage(backend);
  if (!storage) return;

  try {
    storage.removeItem(buildKey(namespace, key));
  } catch {
    // ignore
  }
}

/**
 * Deletes every key that belongs to a namespace.
 * Used by `usePagePersistence.clearPageState()` and logout handlers.
 */
export function clearNamespace(
  namespace: string,
  backend: StorageBackend = "session"
): void {
  const storage = getStorage(backend);
  if (!storage) return;

  try {
    const prefix = buildKey(namespace, "");
    // Collect first to avoid iterator invalidation during removal.
    const victims = Object.keys(storage).filter((k) => k.startsWith(prefix));
    victims.forEach((k) => storage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Deletes every key that was written by this persistence system.
 * Intended for full logout / "clear all data" flows.
 */
export function clearAllPersistence(
  backend: StorageBackend = "session"
): void {
  const storage = getStorage(backend);
  if (!storage) return;

  try {
    const victims = Object.keys(storage).filter((k) => k.startsWith(NS_PREFIX));
    victims.forEach((k) => storage.removeItem(k));
  } catch {
    // ignore
  }
}

// ─── Navigation-preserve flag ─────────────────────────────────────────────────
//
// Problem: we want filters to reset when the user navigates to an unrelated
// page, but persist when they drill into a detail view and come back.
//
// Solution:
//   1. Before pushing to a detail route call `markNavigationPreserve(ns)`.
//      This writes a small flag into the namespace.
//   2. When the list component mounts it calls `consumeNavigationPreserve(ns)`:
//      • Flag found  → delete the flag, keep all other storage (restore filters).
//      • Flag absent → clear the entire namespace (fresh defaults).
//
// F5 reload is safe: `initPersistenceSession()` below wipes all `wp:*` keys
// (including any lingering preserve flag) before any component mounts.

const PRESERVE_KEY = "__preserve__";

/**
 * Sets the preserve flag for `namespace`. Call this immediately before
 * `history.push(detailRoute)` for every navigation that should restore
 * filters on return.
 */
export function markNavigationPreserve(
  namespace: string,
  backend: StorageBackend = "session"
): void {
  writeToStorage(namespace, PRESERVE_KEY, true, backend);
}

/**
 * Reads and removes the preserve flag for `namespace`.
 * Returns `true` if the flag was set (user is returning from a detail page)
 * or `false` if it was absent (fresh navigation → caller should clear the namespace).
 */
export function consumeNavigationPreserve(
  namespace: string,
  backend: StorageBackend = "session"
): boolean {
  const flag = readFromStorage<boolean>(namespace, PRESERVE_KEY, backend);
  if (flag) {
    removeFromStorage(namespace, PRESERVE_KEY, backend);
    return true;
  }
  return false;
}

/**
 * Call this ONCE at app startup (module level in App.tsx, outside any
 * component) to wire up the reload-clear behaviour.
 *
 * Logic:
 *   • If the session stamp stored in sessionStorage does NOT match the
 *     current runtime ID, this is a fresh page load (F5 / direct URL /
 *     new tab). All filter state is wiped so the user starts clean.
 *   • If the stamp matches, we are inside the same JS session (React Router
 *     navigation). Storage is left intact so filters are restored.
 *
 * After the check the new runtime ID is written to sessionStorage so that
 * subsequent React Router navigations (which don't re-execute this module)
 * will always find a matching stamp.
 */
export function initPersistenceSession(
  backend: StorageBackend = "session"
): void {
  const storage = getStorage(backend);
  if (!storage) return;

  try {
    const storedStamp = storage.getItem(SESSION_STAMP_KEY);
    if (storedStamp !== RUNTIME_SESSION_ID) {
      // Mismatch → page was reloaded or this is the very first visit.
      // Clear all filter state so the page starts with fresh defaults.
      clearAllPersistence(backend);
      storage.setItem(SESSION_STAMP_KEY, RUNTIME_SESSION_ID);
    }
    // If stamps match → within-session navigation → do nothing.
  } catch {
    // ignore
  }
}
